// src/components/Services/ServiceOrders/CreateServiceOrderForm.tsx
import { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { useCreateServiceOrder } from '@/hooks/useServiceOrders';
import { useCustomerEquipment } from '@/hooks/useCustomerEquipment';
import { ServiceOrderEquipmentInput } from '@/types/service';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { CustomerCombobox } from '@/components/Shared/CustomerCombobox';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

// ─── Schema ───────────────────────────────────────────────────────────────────
// Criação = só coleta. Peças/serviços são adicionados depois, no passo
// "Gerar Orçamento" (ver GenerateQuoteDialog).

const equipmentSchema = z.object({
  customer_equipment_id: z.string().optional(),
  equipment_name:        z.string().min(1, 'Nome do equipamento obrigatório'),
  brand:                 z.string().optional(),
  model:                 z.string().optional(),
  serial_number:         z.string().optional(),
  reported_issue:        z.string().optional(),
  warranty_status:       z.enum(['in_warranty', 'out_of_warranty']).optional(),
  save_to_customer:      z.boolean().default(false),
});

const formSchema = z.object({
  customer_id:    z.string().min(1, 'Cliente obrigatório'),
  collected_by:   z.string().optional(),
  notes:          z.string().optional(),
  internal_notes: z.string().optional(),
  equipment:      z.array(equipmentSchema).min(1, 'Adicione pelo menos um equipamento'),
});

type FormValues = z.infer<typeof formSchema>;

// ─── Props ────────────────────────────────────────────────────────────────────

interface CreateServiceOrderFormProps {
  onSuccess: (newId: string) => void;
  onCancel: () => void;
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function CreateServiceOrderForm({
  onSuccess,
  onCancel,
}: CreateServiceOrderFormProps) {
  // Todos os hooks ANTES de qualquer early return
  const { company } = useAuth();
  const createMutation = useCreateServiceOrder();
  const [expandedEquipment, setExpandedEquipment] = useState<number[]>([0]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      customer_id:    '',
      collected_by:   '',
      notes:          '',
      internal_notes: '',
      equipment: [{
        equipment_name:   '',
        warranty_status:  'out_of_warranty',
        save_to_customer: false,
      }],
    },
  });

  const watchedCustomerId = form.watch('customer_id');

  const { data: existingEquipment = [] } = useCustomerEquipment(
    watchedCustomerId || null,
  );

  const { fields: equipmentFields, append: appendEquipment, remove: removeEquipment } =
    useFieldArray({ control: form.control, name: 'equipment' });

  const toggleEquipment = (index: number) => {
    setExpandedEquipment((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index],
    );
  };

  const handleAddEquipment = () => {
    const newIndex = equipmentFields.length;
    appendEquipment({
      equipment_name:   '',
      warranty_status:  'out_of_warranty',
      save_to_customer: false,
    });
    setExpandedEquipment((prev) => [...prev, newIndex]);
  };

  const handleSelectExistingEquipment = (index: number, equipId: string) => {
    const found = existingEquipment.find((e) => e.id === equipId);
    if (!found) return;
    form.setValue(`equipment.${index}.customer_equipment_id`, found.id);
    form.setValue(`equipment.${index}.equipment_name`, found.name);
    form.setValue(`equipment.${index}.brand`, found.brand ?? '');
    form.setValue(`equipment.${index}.model`, found.model ?? '');
    form.setValue(`equipment.${index}.serial_number`, found.serial_number ?? '');
  };

  const onSubmit = async (values: FormValues) => {
    try {
      // Criação só-coleta: equipamentos sem itens (orçamento vem depois).
      const equipment = values.equipment.map((equip) => ({
        ...equip,
        items: [],
      }));

      const newId = await createMutation.mutateAsync({
        customerId:    values.customer_id,
        collectedBy:   values.collected_by || undefined,
        notes:         values.notes || undefined,
        internalNotes: values.internal_notes || undefined,
        equipment:     equipment as ServiceOrderEquipmentInput[],
      });

      form.reset();
      setExpandedEquipment([0]);
      onSuccess(newId as string);
    } catch {
      // erro tratado no hook
    }
  };

  return (
    <Form {...form}>
      <div className="space-y-6 w-full">

        {/* Dados gerais */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="customer_id"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Cliente *</FormLabel>
                <FormControl>
                  <CustomerCombobox
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="collected_by"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Recebido por</FormLabel>
                <FormControl>
                  <Input placeholder="Nome de quem recebeu" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Observações</FormLabel>
                <FormControl>
                  <Textarea rows={2} className="resize-none" placeholder="Observações para o cliente" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="internal_notes"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Notas internas</FormLabel>
                <FormControl>
                  <Textarea rows={2} className="resize-none" placeholder="Notas visíveis apenas internamente" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        {/* Equipamentos */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              Equipamentos ({equipmentFields.length})
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddEquipment}
            >
              <Plus className="h-4 w-4 mr-1" />
              Adicionar equipamento
            </Button>
          </div>

          {equipmentFields.map((field, equipIndex) => (
            <EquipmentBlock
              key={field.id}
              equipIndex={equipIndex}
              form={form}
              isExpanded={expandedEquipment.includes(equipIndex)}
              onToggle={() => toggleEquipment(equipIndex)}
              onRemove={equipmentFields.length > 1
                ? () => removeEquipment(equipIndex)
                : undefined}
              existingEquipment={existingEquipment}
              onSelectExisting={(id) => handleSelectExistingEquipment(equipIndex, id)}
              companyId={company?.id ?? ''}
            />
          ))}
        </div>

        {/* Ações */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="button" onClick={form.handleSubmit(onSubmit)} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Criando...' : 'Criar OS'}
          </Button>
        </div>
      </div>
    </Form>
  );
}

// ─── Sub-componente: bloco de equipamento ─────────────────────────────────────

interface EquipmentBlockProps {
  equipIndex: number;
  form: ReturnType<typeof useForm<FormValues>>;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove?: () => void;
  existingEquipment: { id: string; name: string; brand?: string; model?: string; serial_number?: string }[];
  onSelectExisting: (id: string) => void;
  companyId: string;
}

function EquipmentBlock({
  equipIndex,
  form,
  isExpanded,
  onToggle,
  onRemove,
  existingEquipment,
  onSelectExisting,
  companyId: _companyId,
}: EquipmentBlockProps) {
  const equipName = form.watch(`equipment.${equipIndex}.equipment_name`);

  return (
    <div className="border rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between px-4 py-3 bg-muted/40 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {isExpanded
            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
            : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          <span className="text-sm font-medium">
            {equipName || `Equipamento ${equipIndex + 1}`}
          </span>
        </div>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isExpanded && (
        <div className="p-4 space-y-4">
          {existingEquipment.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                Equipamentos anteriores deste cliente:
              </p>
              <Select onValueChange={onSelectExisting}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="Selecionar equipamento existente..." />
                </SelectTrigger>
                <SelectContent>
                  {existingEquipment.map((eq) => (
                    <SelectItem key={eq.id} value={eq.id}>
                      {eq.name}
                      {eq.model ? ` — ${eq.model}` : ''}
                      {eq.serial_number ? ` (S/N: ${eq.serial_number})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name={`equipment.${equipIndex}.equipment_name`}
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel>Nome do equipamento *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: STX 305 ELT" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`equipment.${equipIndex}.brand`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Marca</FormLabel>
                  <FormControl><Input placeholder="Marca" {...field} /></FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`equipment.${equipIndex}.model`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Modelo</FormLabel>
                  <FormControl><Input placeholder="Modelo" {...field} /></FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`equipment.${equipIndex}.serial_number`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nº de série</FormLabel>
                  <FormControl><Input placeholder="S/N" {...field} /></FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`equipment.${equipIndex}.warranty_status`}
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border px-3 h-10 self-end">
                  <FormLabel className="font-normal">Em garantia</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value === 'in_warranty'}
                      onCheckedChange={(checked) =>
                        field.onChange(checked ? 'in_warranty' : 'out_of_warranty')
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`equipment.${equipIndex}.reported_issue`}
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel>Defeito relatado</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      className="resize-none"
                      placeholder="Descreva o defeito relatado pelo cliente"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
